import {
  BoardCirculationAnnotationSchema,
  BoardCirculationSchema,
  PortalPublishedApprovalListResponseSchema,
  PortalPublishedBoardPackListResponseSchema,
  PortalPublishedDownloadResponseSchema,
  PortalPublishedInvoiceListResponseSchema,
  PortalPublishedMeetingListResponseSchema,
  PortalPublishedSignatureListResponseSchema,
  PortalPublishedSupplierItemListResponseSchema,
  type PortalPublishedApproval,
  type PortalPublishedBoardPack,
  type PortalPublishedDownloadResponse,
  type PortalPublishedInvoice,
  type PortalPublishedMeeting,
  type PortalPublishedSignature,
  type PortalPublishedSupplierItem,
  type BoardCirculation,
  type BoardCirculationAnnotation,
  type BoardCirculationAnnotationCreate,
} from "@l30/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { portalFetch } from "./portal-client.js";

const PORTAL_PUBLISHED_SIGNATURES_KEY = ["portal", "published", "signatures"] as const;
const PORTAL_PUBLISHED_MEETINGS_KEY = ["portal", "published", "meetings"] as const;
const PORTAL_PUBLISHED_INVOICES_KEY = ["portal", "published", "invoices"] as const;
const PORTAL_PUBLISHED_APPROVALS_KEY = ["portal", "published", "approvals"] as const;
const PORTAL_PUBLISHED_BOARD_PACKS_KEY = ["portal", "published", "board-packs"] as const;
const PORTAL_PUBLISHED_SUPPLIER_ITEMS_KEY = ["portal", "published", "supplier-items"] as const;

export function usePortalPublishedSignatures() {
  return useQuery<{ signatures: PortalPublishedSignature[]; total: number }>({
    queryKey: PORTAL_PUBLISHED_SIGNATURES_KEY,
    queryFn: () =>
      portalFetch("/portal/signatures", {}, PortalPublishedSignatureListResponseSchema),
    refetchOnWindowFocus: false,
  });
}

export function usePortalPublishedMeetings() {
  return useQuery<{ meetings: PortalPublishedMeeting[]; total: number }>({
    queryKey: PORTAL_PUBLISHED_MEETINGS_KEY,
    queryFn: () => portalFetch("/portal/meetings", {}, PortalPublishedMeetingListResponseSchema),
    refetchOnWindowFocus: false,
  });
}

export function usePortalPublishedInvoices() {
  return useQuery<{ invoices: PortalPublishedInvoice[]; total: number }>({
    queryKey: PORTAL_PUBLISHED_INVOICES_KEY,
    queryFn: () => portalFetch("/portal/invoices", {}, PortalPublishedInvoiceListResponseSchema),
    refetchOnWindowFocus: false,
  });
}

export function usePortalPublishedApprovals() {
  return useQuery<{ approvals: PortalPublishedApproval[]; total: number }>({
    queryKey: PORTAL_PUBLISHED_APPROVALS_KEY,
    queryFn: () => portalFetch("/portal/approvals", {}, PortalPublishedApprovalListResponseSchema),
    refetchOnWindowFocus: false,
  });
}

export function usePortalPublishedBoardPacks() {
  return useQuery<{ boardPacks: PortalPublishedBoardPack[]; total: number }>({
    queryKey: PORTAL_PUBLISHED_BOARD_PACKS_KEY,
    queryFn: () =>
      portalFetch("/portal/board-packs", {}, PortalPublishedBoardPackListResponseSchema),
    refetchOnWindowFocus: false,
  });
}

export function usePortalPublishedSupplierItems() {
  return useQuery<{ supplierItems: PortalPublishedSupplierItem[]; total: number }>({
    queryKey: PORTAL_PUBLISHED_SUPPLIER_ITEMS_KEY,
    queryFn: () =>
      portalFetch("/portal/supplier-items", {}, PortalPublishedSupplierItemListResponseSchema),
    refetchOnWindowFocus: false,
  });
}

export function useDownloadPortalSignatureArtifact() {
  return useMutation<
    PortalPublishedDownloadResponse,
    Error,
    { id: string; artifact: "signed" | "certificate" }
  >({
    mutationFn: async ({ id, artifact }) => {
      const response = await portalFetch(
        `/portal/signatures/${id}/download/${artifact}`,
        {},
        PortalPublishedDownloadResponseSchema,
      );
      triggerDownload(response);
      return response;
    },
  });
}

export function useDownloadPortalMeeting() {
  return useMutation<PortalPublishedDownloadResponse, Error, string>({
    mutationFn: async (id) => {
      const response = await portalFetch(
        `/portal/meetings/${id}/download`,
        {},
        PortalPublishedDownloadResponseSchema,
      );
      triggerDownload(response);
      return response;
    },
  });
}

export function useDownloadPortalBoardPack() {
  const qc = useQueryClient();
  return useMutation<PortalPublishedDownloadResponse, Error, string>({
    mutationFn: async (id) => {
      const response = await portalFetch(
        `/portal/board-packs/${id}/download`,
        {},
        PortalPublishedDownloadResponseSchema,
      );
      triggerDownload(response);
      return response;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: PORTAL_PUBLISHED_BOARD_PACKS_KEY }),
  });
}

export function useAcknowledgePortalBoardPack() {
  const qc = useQueryClient();
  return useMutation<BoardCirculation, Error, string>({
    mutationFn: (id) =>
      portalFetch(
        `/portal/board-packs/${id}/acknowledge`,
        {
          method: "POST",
          body: JSON.stringify({}),
        },
        BoardCirculationSchema,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: PORTAL_PUBLISHED_BOARD_PACKS_KEY }),
  });
}

export function useCreatePortalBoardPackAnnotation() {
  const qc = useQueryClient();
  return useMutation<
    BoardCirculationAnnotation,
    Error,
    { id: string; data: BoardCirculationAnnotationCreate }
  >({
    mutationFn: ({ id, data }) =>
      portalFetch(
        `/portal/board-packs/${id}/annotations`,
        {
          method: "POST",
          body: JSON.stringify(data),
        },
        BoardCirculationAnnotationSchema,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: PORTAL_PUBLISHED_BOARD_PACKS_KEY }),
  });
}

export function useDownloadPortalSupplierItem() {
  return useMutation<PortalPublishedDownloadResponse, Error, string>({
    mutationFn: async (id) => {
      const response = await portalFetch(
        `/portal/supplier-items/${id}/download`,
        {},
        PortalPublishedDownloadResponseSchema,
      );
      triggerDownload(response);
      return response;
    },
  });
}

function triggerDownload(response: PortalPublishedDownloadResponse) {
  const anchor = document.createElement("a");
  anchor.href = response.downloadUrl;
  anchor.download = response.filename;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
}
